/**
 * 前台页入场（无粉屏）：壳层滑入 + paper 分批入场。
 * 侧栏（含价目表）只跟壳层滑一次，禁止嵌套位移。
 */
import { PAGE_ENTER_TIMING, REVEAL_DISTANCE_PX } from '@/config/preload';
import { usePageReveal } from '@/hooks/use-page-reveal';
import { applyFxForceClass, prefersReducedMotion } from '@/utils/motion-pref';

function applyCssVars(): void {
  const s = document.documentElement.style;
  s.setProperty('--reveal-distance', `${REVEAL_DISTANCE_PX}px`);
  s.setProperty('--reveal-dur', `${PAGE_ENTER_TIMING.revealMs / 1000}s`);
  s.setProperty('--shell-settle-dur', `${PAGE_ENTER_TIMING.shellMs / 1000}s`);
}

function resetEnterDom(): void {
  document.body.classList.remove('is-page-leaving', 'is-enter-done');
  document.documentElement.classList.remove('page-enter-ready');
  document.documentElement.classList.add('page-enter-pending');
  document.querySelectorAll<HTMLElement>('[data-reveal]').forEach((el) => {
    el.classList.remove('is-revealed');
    delete el.dataset.revealBound;
  });
  document.querySelectorAll('.motion-reveal').forEach((el) => {
    el.classList.remove('motion-in');
  });
}

function settleShell(): void {
  document.documentElement.classList.remove('page-enter-pending');
  document.documentElement.classList.add('page-enter-ready');
  document.body.classList.add('is-enter-done');
  document.body.classList.remove('is-page-leaving');
}

export interface MountPageEnterOptions {
  force?: boolean;
}

export function mountPageEnter(opts: MountPageEnterOptions = {}): void {
  if (!opts.force && document.documentElement.dataset.pageEnter === '1') return;
  document.documentElement.dataset.pageEnter = '1';

  applyFxForceClass();
  applyCssVars();

  if (opts.force) resetEnterDom();
  else if (!document.documentElement.classList.contains('page-enter-pending')) {
    document.documentElement.classList.add('page-enter-pending');
  }

  const reveal = usePageReveal();
  reveal.prepare();

  if (prefersReducedMotion()) {
    settleShell();
    reveal.start({ immediate: true });
    return;
  }

  // 双 rAF + 读布局：确保 pending 起始态已绘制，再切 ready（消除「卡一下再动」）
  requestAnimationFrame(() => {
    void document.body.offsetWidth;
    requestAnimationFrame(() => {
      settleShell();
      window.setTimeout(() => {
        reveal.start();
        window.setTimeout(() => reveal.destroy(), 12000);
      }, PAGE_ENTER_TIMING.revealLeadMs);
    });
  });
}

export function bindPageEnterOnShow(): void {
  window.addEventListener('pageshow', (e) => {
    document.body.classList.remove('is-page-leaving');
    if (e.persisted) {
      document.documentElement.dataset.pageEnter = '';
      mountPageEnter({ force: true });
    }
  });
}
